import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppThunk } from "store/store";
import { API_ENDPOINTS } from "config/apiConfig";
import { axiosInstance } from "config/axiosConfig";
import { showLoading, hideLoading } from "./loadingSlice";
import { ResponseStatusEnum } from "common/enums/ResponseStatus.enum";

interface LoginState {
  userData: any;
  resetPasswordData: any;
  errorResetPassword: any;
  changePasswordData: any;
  errorChangePassword: any;
  error: any | null;
}

const initialState: LoginState = {
  userData: null,
  resetPasswordData: null,
  errorResetPassword: null,
  changePasswordData: null,
  errorChangePassword: null,
  error: null,
};

export const authSlice = createSlice({
  name: "login",
  initialState,
  reducers: {
    loginSuccess: (state, action: PayloadAction<any>) => {
      state.userData = action.payload.data.user;
      localStorage.setItem("access_token", action.payload.data.access_token);
    },
    changePasswordSuccess: (state, action: PayloadAction<any>) => {
      state.changePasswordData = action.payload;
    },
    changePasswordFailure: (state, action: PayloadAction<any>) => {
      state.changePasswordData = null;
      state.errorChangePassword = action.payload;
    },
    resetPasswordSuccess: (state, action: PayloadAction<any>) => {
      state.resetPasswordData = action.payload;
    },
    resetPasswordFailure: (state, action: PayloadAction<any>) => {
      state.resetPasswordData = null;
      state.errorResetPassword = action.payload;
    },
    loginFailure: (state, action: PayloadAction<any>) => {
      localStorage.removeItem("access_token");
      state.error = action.payload;
    },
    changeCompanyName: (state, action: PayloadAction<string>) => {
      state.userData.company_name = action.payload;
    },
    cleanData: (state) => {
      state.userData = null;
      state.error = null;
      state.resetPasswordData = null;
      state.errorResetPassword = null;
      state.changePasswordData = null;
      state.errorChangePassword = null;
      localStorage.removeItem("access_token");
    },
    setUserData: (state, action: PayloadAction<any>) => {
      state.userData = action.payload;
    },
  },
});

export const {
  loginSuccess,
  loginFailure,
  cleanData,
  setUserData,
  resetPasswordSuccess,
  resetPasswordFailure,
  changePasswordSuccess,
  changePasswordFailure,
  changeCompanyName,
} = authSlice.actions;

export const fetchLogin =
  (loginQuery: { email: string; password: string }): AppThunk =>
  async (dispatch) => {
    dispatch(showLoading());
    try {
      const response = await axiosInstance.post(API_ENDPOINTS.LOGIN, {
        ...loginQuery,
      });
      dispatch(loginSuccess(response.data));
      return response.data;
    } catch (error: any) {
      dispatch(loginFailure(error));
    } finally {
      dispatch(hideLoading());
    }
  };

export const fetchLoginAdmin =
  (loginQuery: { email: string; password: string }): AppThunk =>
  async (dispatch) => {
    dispatch(showLoading());
    try {
      const response = await axiosInstance.post(API_ENDPOINTS.LOGIN_ADMIN, {
        ...loginQuery,
      });
      dispatch(loginSuccess(response.data));
      return response.data;
    } catch (error: any) {
      dispatch(loginFailure(error.message));
    } finally {
      dispatch(hideLoading());
    }
  };

export const fetchUser = (): AppThunk => async (dispatch, getState) => {
  try {
    const response = await axiosInstance.get(API_ENDPOINTS.GET_ME);
    dispatch(setUserData(response.data.data));
  } catch (error: any) {
    if (
      error.response &&
      error.response.status === ResponseStatusEnum.UNAUTHORIZED
    ) {
      dispatch(loginFailure(error.message));
    }
  }
};

export const fetchResetPassword =
  (email: string): AppThunk =>
  async (dispatch) => {
    dispatch(showLoading());
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.RESET_PASSWORD, {
        params: { email },
      });
      dispatch(resetPasswordSuccess(response.data));
      return response.data;
    } catch (error: any) {
      dispatch(resetPasswordFailure(error.response.data));
    } finally {
      dispatch(hideLoading());
    }
  };

export const fetchHandleResetPassword =
  (token: string, password_new: string): AppThunk =>
  async (dispatch) => {
    dispatch(showLoading());
    try {
      const response = await axiosInstance.put(
        `${API_ENDPOINTS.RESET_PASSWORD_HANDLE}`,
        {
          token: token,
          password_new: password_new,
        },
      );
      dispatch(changePasswordSuccess(response.data));
      return response.data;
    } catch (error: any) {
      dispatch(changePasswordFailure(error.response.data));
    } finally {
      dispatch(hideLoading());
    }
  };

export default authSlice.reducer;
